// All accounts' Filters dropdown: Sprint, Pool, Strategist and Vertical, each a single select, inside
// the shared shell (`FiltersDropdown.tsx`, also My Accounts'). Every active one is also a removable
// chip beside the button, so a filtered view is never hidden behind it. Sprint and Pool read the
// same options as My Accounts' (`sprints.ts`). The rules are `allAccountsFilters.ts`'s.

import type { AllAccountsFilters, OwnerOption } from './allAccountsFilters';
import { clearDropdownFilters, dropdownFilterCount } from './allAccountsFilters';
import type { PoolChoice } from './sprints';
import { FilterChip, FiltersDropdown } from './FiltersDropdown';
import { formatCount } from '../lib/format';

type SelectKey = 'sprint' | 'pool' | 'owner_email' | 'vertical';

interface SelectOption {
  value: string;
  name: string;
  // How many book rows hold it, for the sprint and pool options.
  count?: number;
}

interface SelectSpec {
  key: SelectKey;
  label: string;
  options: SelectOption[];
}

function choiceOptions(choices: PoolChoice[]): SelectOption[] {
  return choices.map(choice => ({ value: choice.key, name: choice.label, count: choice.count }));
}

function optionText(option: SelectOption): string {
  return option.count === undefined ? option.name : `${option.name} (${formatCount(option.count)})`;
}

// A chip names the option; a value the book no longer offers (an old link) shows as is.
function chipText(spec: SelectSpec, value: string): string {
  return spec.options.find(candidate => candidate.value === value)?.name ?? value;
}

function SingleSelect({
  spec, value, onChange,
}: {
  spec: SelectSpec;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = `aa-${spec.key}`;
  return (
    <div className="ffield">
      <label className="flab" htmlFor={id}>{spec.label}</label>
      <select id={id} value={value} onChange={event => onChange(event.target.value)}>
        <option value="">Any</option>
        {/* An old link's value the book no longer has, so the select can still show and clear it. */}
        {value && !spec.options.some(option => option.value === value) ? <option value={value}>{value}</option> : null}
        {spec.options.map(option => <option key={option.value} value={option.value}>{optionText(option)}</option>)}
      </select>
    </div>
  );
}

export function AllAccountsFilterControls({
  filters, onChange, isOpen, onOpenChange, sprints, pools, owners, verticals,
}: {
  filters: AllAccountsFilters;
  onChange: (next: AllAccountsFilters) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  sprints: PoolChoice[];
  pools: PoolChoice[];
  owners: OwnerOption[];
  verticals: string[];
}) {
  // The panel's groups, in My Accounts' order: sprint and pool first, then the company.
  const poolSpecs: SelectSpec[] = [
    { key: 'sprint', label: 'Sprint', options: choiceOptions(sprints) },
    { key: 'pool', label: 'Pool', options: choiceOptions(pools) },
  ];
  const companySpecs: SelectSpec[] = [
    { key: 'owner_email', label: 'Strategist', options: owners.map(owner => ({ value: owner.email, name: owner.name })) },
    { key: 'vertical', label: 'Vertical', options: verticals.map(vertical => ({ value: vertical, name: vertical })) },
  ];
  const groups = [
    { label: 'Sprint & pool', specs: poolSpecs },
    { label: 'Company', specs: companySpecs },
  ];

  // A select with nothing to offer and nothing chosen would be a dead row.
  const isShown = (spec: SelectSpec) => spec.options.length > 0 || Boolean(filters[spec.key]);
  const allSpecs = [...poolSpecs, ...companySpecs];

  const chips = allSpecs.filter(spec => filters[spec.key]).map(spec => {
    const value = chipText(spec, filters[spec.key]);
    return (
      <FilterChip
        key={spec.key}
        label={spec.label}
        value={value}
        removeLabel={`Remove the ${spec.label} filter, ${value}`}
        onRemove={() => onChange({ ...filters, [spec.key]: '' })}
      />
    );
  });

  return (
    <FiltersDropdown
      activeCount={dropdownFilterCount(filters)}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClearAll={() => onChange(clearDropdownFilters(filters))}
      note="Each filter narrows. The chips on the bar stay as they are."
      chips={chips}
    >
      {groups.map(group => {
        const shownSpecs = group.specs.filter(isShown);
        if (!shownSpecs.length) return null;
        return (
          <div className="fgroup" role="group" aria-label={group.label} key={group.label}>
            <span className="fgrouplab">{group.label}</span>
            <div className="fgrid">
              {shownSpecs.map(spec => (
                <SingleSelect key={spec.key} spec={spec} value={filters[spec.key]}
                  onChange={next => onChange({ ...filters, [spec.key]: next })} />
              ))}
            </div>
          </div>
        );
      })}
    </FiltersDropdown>
  );
}
